import { Fragment, useCallback, useEffect, useRef } from 'react'
import { Document } from '@contentful/rich-text-types'
import { Box, Typography } from '@mui/material'

import { Settings } from '@ecm/config/Settings'
import { convertToComponent } from '@ecm/data/Contentful'
import useScrollPosition from '@ecm/ui/hook/UseScrollPosition'
import debounce from '@ecm/utils/debounce'

export enum SectionType {
  Summary = 'summary',
  Challenges = 'challenges',
  Problem = 'problem',
  Solution = 'solution',
  Results = 'results',
}

export const sectionTitle = {
  [SectionType.Summary]: 'Summary',
  [SectionType.Challenges]: 'Challenges',
  [SectionType.Problem]: 'The problem at large',
  [SectionType.Solution]: 'Solutions',
  [SectionType.Results]: 'Results',
}

type SectionElement = {
  id: SectionType
} & HTMLElement

type CaseStudyArticleProps = {
  activeSection: SectionType
  summaryDescription: Document
  quote: string
  challengesDescription: Document
  theProblemAtLargeDescription: Document
  solutionDescription: Document
  resultsDescription: Document
  isListeningScroll: boolean
  setListeningScroll: (value: boolean) => void
  onScroll: (id: SectionType) => void
}

const CaseStudyArticle = ({
  activeSection,
  summaryDescription,
  quote,
  challengesDescription,
  theProblemAtLargeDescription,
  solutionDescription,
  resultsDescription,
  isListeningScroll,
  setListeningScroll,
  onScroll,
}: CaseStudyArticleProps) => {
  const content = {
    [SectionType.Summary]: summaryDescription,
    [SectionType.Challenges]: challengesDescription,
    [SectionType.Problem]: theProblemAtLargeDescription,
    [SectionType.Solution]: solutionDescription,
    [SectionType.Results]: resultsDescription,
  }

  const sectionRefs = useRef<SectionElement[]>([])
  const scrollPosition = useScrollPosition()

  useEffect(() => {
    if (!isListeningScroll) {
      return
    }
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight) {
      onScroll(SectionType.Results)
      return
    }
    sectionRefs.current.forEach(section => {
      if (section.getBoundingClientRect().top - (Settings.topMenuHeight + 1) < 0) {
        onScroll(section.id)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollPosition, isListeningScroll])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const verify = useCallback(
    debounce(() => {
      setListeningScroll(true)
    }),
    []
  )

  useEffect(() => {
    if (isListeningScroll) {
      return
    }
    sectionRefs.current.forEach(section => {
      if (section.id === activeSection) {
        section.scrollIntoView({ behavior: 'smooth' })
      }
    })
    verify()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection])

  return (
    <Box>
      {Object.entries(content).map((entry, i) => {
        const [id, description] = entry as [SectionType, Document] // type assertion is needed because Object.entries overwrites key type
        return (
          <Fragment key={id}>
            <Typography
              ref={(el: SectionElement) => (sectionRefs.current[i] = el)}
              variant="h4"
              mb="26px"
              sx={{
                '&:not(:first-of-type)': { mt: '64px' },
                scrollMarginTop: `${Settings.topMenuHeight}px`,
              }}
              id={id}
            >
              {sectionTitle[id]}
            </Typography>
            {convertToComponent(description, 'p3')}

            {i === 0 && (
              <Typography
                variant="h4"
                component="p"
                position="relative"
                m={{ md: '150px 0 80px 105px', xs: '150px 0 80px 0' }}
                sx={{
                  '&:before': {
                    content: 'url(/images/icon/quote-grey.svg)',
                    left: { md: '-100px', xs: '0' },
                    position: 'absolute',
                    top: '-75px',
                    zIndex: '-1',
                  },
                }}
              >
                {quote}
              </Typography>
            )}
          </Fragment>
        )
      })}
    </Box>
  )
}

export default CaseStudyArticle
